import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ShiftTypeColumn } from "./ShiftTypeColumn";
import type { AlertSeverity, AlertType, DepartmentAlertDayView } from "./types";
import { ALERT_TYPE_COLORS, ALERT_TYPE_I18N_KEYS, getSeverityRank, keyboardClickProps, SEVERITY_COLORS, SEVERITY_I18N_KEYS, SEVERITY_ICONS } from "./utils";

type Props = {
	day: DepartmentAlertDayView;
	isExpanded: boolean;
	onToggle: (date: string) => void;
	showPerTypeCount: boolean;
};

export const DateSection: FC<Props> = ({ day, isExpanded, onToggle, showPerTypeCount }) => {
	const { t } = useTranslation();
	const formatted = day.date.toFormat("ccc, LLL dd, yyyy");

	return (
		<Box>
			<Box
				onClick={() => onToggle(day.date.toISODate() ?? "")}
				{...keyboardClickProps(() => onToggle(day.date.toISODate() ?? ""))}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					bgcolor: "white",
					borderRadius: "8px",
					border: "1px solid",
					borderColor: "grey.200",
					px: 2,
					py: 1.5,
					"&:hover": { bgcolor: "grey.50" },
					transition: "background-color 0.2s",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
					}}
				>
					<KeyboardArrowDownIcon fontSize="small" sx={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }} />
					<Typography
						variant="body1"
						sx={{
							fontWeight: 700,
						}}
					>
						{formatted}
					</Typography>

					{/* Divider */}
					<Box sx={{ width: "1px", height: 24, bgcolor: "grey.300" }} />

					{/* Severity badges — sorted critical → warning → major (most urgent first) */}
					{Object.entries(day.severityCounts)
						.filter(([, count]) => count > 0)
						.sort(([a], [b]) => getSeverityRank(b as AlertSeverity) - getSeverityRank(a as AlertSeverity))
						.map(([sev, count]) => {
							const colors = SEVERITY_COLORS[sev as AlertSeverity];
							const SevIcon = SEVERITY_ICONS[sev as AlertSeverity];
							return (
								<Chip
									key={sev}
									size="small"
									label={
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
											}}
										>
											<SevIcon sx={{ fontSize: 12, color: colors.accent }} />
											{count} {t(SEVERITY_I18N_KEYS[sev as AlertSeverity])}
										</Box>
									}
									variant="outlined"
									sx={{
										bgcolor: colors.bg,
										color: colors.text,
										fontWeight: 600,
										fontSize: "0.7rem",
										minWidth: 100,
										justifyContent: "center",
										borderColor: colors.accent,
									}}
								/>
							);
						})}
				</Box>

				{showPerTypeCount ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
						}}
					>
						{Object.entries(day.alertTypeCounts)
							.filter(([, count]) => count > 0)
							.map(([type, count]) => {
								const colors = ALERT_TYPE_COLORS[type as AlertType];
								return (
									<Chip
										key={type}
										size="small"
										label={
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
												}}
											>
												<Typography component="span" sx={{ fontWeight: 700, fontSize: "0.7rem" }}>
													{count}
												</Typography>
												<Typography component="span" sx={{ fontSize: "0.6rem" }}>
													{t(ALERT_TYPE_I18N_KEYS[type as AlertType])}
												</Typography>
											</Box>
										}
										sx={{ bgcolor: colors.chipBg, color: colors.text, height: 22 }}
									/>
								);
							})}
					</Box>
				) : (
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
						}}
					>
						{t(day.totalAlerts === 1 ? "alerts.count" : "alerts.count_plural", { count: day.totalAlerts })}
					</Typography>
				)}
			</Box>
			<Collapse in={isExpanded} unmountOnExit>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						gap: 2,
						mt: 1,
						overflowX: "auto",
					}}
				>
					{day.shiftTypes.map((st) => (
						<ShiftTypeColumn key={st.label} shiftType={st} />
					))}
				</Box>
			</Collapse>
		</Box>
	);
};
